import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMars, faVenus } from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import { getOptimizedEventImage } from "../utils/imageUtils";
import { useEventImage } from "../utils/useEventImage";
import { RunnerDetailsModal } from "../components/RunnerDetailsModal";
import { useEventId } from "../EventContext";
import { Button, InputField, PageLoader } from "@vionixasia/shared-components";
import { CategoryPicker } from "../components/CategoryPicker";
import { HeroStatus } from "../components/HeroStatus";
import { getFlagCode } from "@vionixasia/shared-components/lib/countryCodes";
import { finishDisplay } from "../utils/finishTime";

// Capitalize the first letter of each word
const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Convert nationality to ISO flag code and display name
// Convert time to seconds for sorting
// Unranked runners come back from the API as overallRank -1, and -1 is truthy:
// `rank || Infinity` yields -1, which sorts them ABOVE the winner. Only a
// positive rank is a real placing.
const rankOrder = (r) => (r?.overallRank > 0 ? r.overallRank : Infinity);

const timeToSeconds = (time) => {
  if (!time) return Infinity;
  
  // Handle COT format: "COT (15:47:54) / 09:42:54" - extract duration after "/"
  if (time.includes("COT")) {
    const parts = time.split("/");
    if (parts.length === 2) {
      const duration = parts[1].trim();
      const [h, m, s] = duration.split(":").map(Number);
      if (!isNaN(h) && !isNaN(m) && !isNaN(s)) {
        return h * 3600 + m * 60 + s;
      }
    }
    // Fallback: try to extract time from COT (HH:MM:SS) format
    const cotMatch = time.match(/COT \((\d{2}:\d{2}:\d{2})\)/);
    if (cotMatch) {
      const [h, m, s] = cotMatch[1].split(":").map(Number);
      return h * 3600 + m * 60 + s;
    }
    return Infinity;
  }
  
  const [h, m, s] = time.split(":").map(Number);
  if (isNaN(h) || isNaN(m) || isNaN(s)) return Infinity;
  return h * 3600 + m * 60 + s;
};

// Format gender with icon
const formatGender = (gender) => {
  if (!gender || typeof gender !== "string") return "-";
  const normalizedGender = gender.toLowerCase();
  if (normalizedGender === "male" || normalizedGender === "m")
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" /> Male
      </>
    );
  if (normalizedGender === "female" || normalizedGender === "f")
    return (
      <>
        <FontAwesomeIcon icon={faVenus} className="mr-1" /> Female
      </>
    );
  return "-";
};

// Format timestamp to local time
const formatTime = (timestamp, isCOT = false) => {
  if (!timestamp) return { time: "-", isCOT };
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return { time: "-", isCOT };
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";
  const options = {
    timeZone: userTimeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };
  const timeStr = date.toLocaleTimeString("en-GB", options);
  return { time: timeStr, isCOT };
};

const cleanFinishSegment = (value) => {
  if (value == null) return "";
  let str = String(value).trim();
  if (!str) return "";
  const cotMatch = str.match(/^COT\s*\((.*)\)$/i);
  if (cotMatch) {
    const inner = cleanFinishSegment(cotMatch[1]);
    return inner ? `COT (${inner})` : "COT (-)";
  }
  if (str.includes("/")) {
    const parts = str.split("/");
    str = parts[parts.length - 1].trim();
  }
  return str.replace(/\.\d+$/, "");
};

const getFinishDisplay = (...candidates) => {
  for (const candidate of candidates) {
    const cleaned = cleanFinishSegment(candidate);
    if (cleaned) return cleaned;
  }
  return "-";
};

// Get hero section details
const getHeroDetails = (selectedCategory, categories, eventName, eventDate) => {
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";
  const now = moment().tz(userTimeZone);
  if (selectedCategory === "") {
    const anyOngoing = categories.some((cat) => {
      const cutOff = cat.cutOffTime?.toDate
        ? moment(cat.cutOffTime.toDate()).tz(userTimeZone)
        : moment(cat.cutOffTime).tz(userTimeZone);
      return cutOff.isValid() && now.isBefore(cutOff);
    });
    return {
      title: eventName,
      dateTime: "Multiple Dates",
      startTime: null,
      cutOffTime: null,
      isCOT: !anyOngoing,
    };
  }

  const category = categories.find((cat) => cat.id === selectedCategory);
  if (!category) {
    return {
      title: "Unknown Category",
      dateTime: "Unknown Date",
      startTime: null,
      cutOffTime: null,
      isCOT: false,
    };
  }

  // A buzzer-timed category has no gun start, so a missing startTime is normal:
  // show the category's name and the event's own date (as Results.jsx does).
  if (!category.startTime) {
    const eventDay = eventDate ? moment(eventDate) : null;
    return {
      title: category.name?.trim() || "Unknown Category",
      dateTime: eventDay?.isValid() ? eventDay.format("DD MMM YYYY") : "Unknown Date",
      startTime: null,
      cutOffTime: null,
      isCOT: false,
    };
  }

  const startTime = category.startTime.toDate
    ? moment(category.startTime.toDate()).tz(userTimeZone)
    : moment(category.startTime).tz(userTimeZone);
  const cutOffTime = category.cutOffTime.toDate
    ? moment(category.cutOffTime.toDate()).tz(userTimeZone)
    : moment(category.cutOffTime).tz(userTimeZone);

  if (!startTime.isValid()) {
    return {
      title: category.name?.trim() || "Unknown Category",
      dateTime: "Invalid Date",
      startTime: null,
      cutOffTime: null,
      isCOT: false,
    };
  }

  const isCOT = cutOffTime.isValid() && now.isAfter(cutOffTime);

  return {
    title: category.name?.trim() || "Unknown Category",
    dateTime: startTime.format("DD MMM YYYY [at] HH:mm"),
    startTime: category.startTime,
    cutOffTime: category.cutOffTime,
    isCOT,
  };
};

// Stopwatch Component
const Stopwatch = ({
  startTime,
  cutOffTime,
  eventStatus,
  categoryName,
  isCOT,
}) => {
  const [elapsedTime, setElapsedTime] = useState("00:00:00");
  const [isCOTState, setIsCOTState] = useState(isCOT || false);
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";

  useEffect(() => {
    if (!startTime || eventStatus !== "ongoing") {
      setIsCOTState(isCOT || false);
      return;
    }

    const start = startTime.toDate
      ? moment(startTime.toDate()).tz(userTimeZone)
      : moment(startTime).tz(userTimeZone);
    const cutOff = cutOffTime.toDate
      ? moment(cutOffTime.toDate()).tz(userTimeZone)
      : moment(cutOffTime).tz(userTimeZone);

    if (!start.isValid()) {
      setIsCOTState(isCOT || false);
      return;
    }

    const updateTime = () => {
      const now = moment().tz(userTimeZone);
      const duration = moment.duration(now.diff(start));
      const hours = Math.floor(duration.asHours()).toString().padStart(2, "0");
      const minutes = duration.minutes().toString().padStart(2, "0");
      const seconds = duration.seconds().toString().padStart(2, "0");
      setElapsedTime(`${hours}:${minutes}:${seconds}`);
      if (cutOff.isValid() && now.isAfter(cutOff)) {
        setIsCOTState(true);
      } else {
        setIsCOTState(false);
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [startTime, cutOffTime, eventStatus, isCOT, userTimeZone]);

  if (!startTime || eventStatus !== "ongoing") {
    if (isCOTState) {
      return <HeroStatus state="finished" category={categoryName || "All Categories"} />;
    }
    return null;
  }

  return (
    <HeroStatus state={isCOTState ? "finished" : "ongoing"} time={elapsedTime} category={categoryName || "Unknown Category"} />
  );
};

// Hero Component
const Hero = ({ selectedCategory, categories, eventName, eventDate, eventStatus, heroImage, location, source, eventId, slug }) => {
  const { title, dateTime, startTime, cutOffTime, isCOT } = getHeroDetails(
    selectedCategory,
    categories,
    eventName,
    eventDate
  );
  const imageSrc = useEventImage(slug || eventId, heroImage);

  return (
    <div
      className="relative w-full h-[30vh] md:h-[60vh] rounded-card overflow-hidden"
      data-source={source}
    >
      {imageSrc && (
        <img
          src={imageSrc}
          alt="Event Hero"
          className="absolute inset-0 w-full h-full object-cover z-0"
          fetchpriority="high"
          loading="eager"
          decoding="sync"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-white/10">
        {/* Logo logic kept hidden as before */}

        
        {/* Title & Location: Mobile Top Center, Desktop Bottom Left */}
        <div className="hidden md:block md:absolute md:top-auto md:bottom-4 md:left-4 md:right-auto md:text-left z-10">
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-wider text-white drop-shadow-lg">
            {title}
            </h2>
            <p className="hidden md:block text-md md:text-xl md:mt-1 text-white drop-shadow-md">
            {location || "Indonesia"} • {dateTime}
            </p>
        </div>
        <Stopwatch
          startTime={startTime}
          cutOffTime={cutOffTime}
          eventStatus={eventStatus}
          categoryName={title}
          isCOT={isCOT}
        />
      </div>
    </div>
  );
};


// Top3Table Component (Nationality DIHAPUS)
const Top3Table = ({ title, results, onNameClick }) => (
  <div className="w-full md:w-[calc(50%-0.5rem)] bg-white rounded-card border border-slate-200 overflow-x-auto">
    <h2 className="text-lg font-semibold text-gray-900 mb-4 px-4 pt-4">
      {title}
    </h2>
    {/* table-fixed on mobile so the rank column holds the width declared below;
        table-auto would let it stretch and push the name further right again. */}
    <table className="min-w-full table-fixed md:table-auto border-collapse">
      <thead className="bg-slate-50 border-y border-slate-200">
        <tr>
          {/* Mobile geometry matches the overall result card, which insets its
              table by 8px (the card's own p-2) and then uses a 48px rank column.
              Reproduce that as 8px of left padding + w-12, so the name in both
              tables starts at the same 56px. Desktop keeps px-2 and 60px. */}
          <th className="w-14 pl-2 pr-0 md:w-[60px] md:px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            Rank
          </th>
          <th className="px-0 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:max-w-[150px]">
            Name
          </th>
          {/* Pinned on mobile like the overall card's finish column, so table-fixed
              gives the remaining width to the name instead. pr-6 = that card's pr-4
              plus its 8px inset, so the times end on the same right edge too. */}
          <th className="w-24 pr-6 pl-0 md:w-auto md:px-4 py-3 text-right md:text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Finish
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-slate-200">
        {results.length === 0 ? (
          <tr>
            <td colSpan={3} className="px-4 py-3 text-center text-sm text-gray-600">
              No data available
            </td>
          </tr>
        ) : (
          results.map((result, index) => (
            <tr
              key={`top3-${result.bib || index}`}
              className="hover:bg-slate-50 transition-colors"
            >
              <td className="w-14 pl-2 pr-0 md:w-[60px] md:px-2 py-3 text-sm text-gray-600 text-center">
                {result.genderRank > 0 ? (
                  <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 bg-slate-50 text-slate-700 border border-slate-200 rounded-full font-bold text-xs">
                    {result.genderRank}
                  </span>
                ) : (
                  <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 bg-slate-50 text-slate-700 border border-slate-200 rounded-full font-bold text-xs">
                    {index + 1}
                  </span>
                )}
              </td>
              <td
                className="px-0 md:px-4 py-3 text-sm font-bold text-gray-900 uppercase max-w-0 md:max-w-[150px] truncate cursor-pointer hover:text-teal-600"
                onClick={() => onNameClick(result)}
              >
                {capitalizeWords(result.name) || "-"}
              </td>
              <td className="w-24 pr-6 pl-0 md:w-auto md:px-4 py-3 text-right md:text-left text-sm font-semibold text-slate-600">
                {getFinishDisplay(result.finishDuration)}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// Fallback only, for events saved before bands were configurable.
const AGE_GROUPS = ["35-39", "40-44", "45-49", "50-54", "55-59", "60-64", "65+"];

// Banding lives on the server, which knows each event's configured ranges and
// already returns the grouped buckets. A second copy here could only ever
// disagree with it, which is how a 16-24 event ended up rendering 35-39.

// Main Component
export const ResultsAge = ({ initialEventData }) => {
  const { realEventId: eventId, slug } = useEventId();
  const navigate = useNavigate();
  const [state, setState] = useState(() => {
    const initialState = {
      rankedResults: [],
      top3Male: [],
      top3Female: [],
      top3AgeGroups: {},
      loading: true,
      totalResults: 0,
      selectedCategory: localStorage.getItem("smm_last_category") || "7K",
      categories: [],
      eventName: "SM Marathon",
      eventStatus: "ongoing",
      livetrackSlug: null,
      showMap: false,
      viewMode: "gender",
      error: null,
      heroImage: null,
      location: "Indonesia",
      source: "INITIAL",
    };

    try {
      const cached = localStorage.getItem(`cached_event_${eventId}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        // Rehydrate dates if necessary or rely on existing logic
        // The existing logic inside Hero/components usually handles strings via moment() or new Date()
        return {
          ...initialState,
          ...parsed,
          loading: false, // Show cached content immediately
          source: "CACHE",
        };
      }
    } catch (e) {
      console.warn("Failed to load cached event data", e);
    }

    // Fallback: pull heroImage from landing page cache so hero is never
    // a placeholder even on first direct visit to this URL.
    try {
      const landingRaw = localStorage.getItem("cached_landing_events");
      if (landingRaw) {
        const events = JSON.parse(landingRaw);
        const match = events.find(
          (e) => e.eventId === eventId || e.slug === eventId
        );
        if (match?.heroImage) initialState.heroImage = match.heroImage;
      }
    } catch {}

    if (initialEventData) {
      const normalized = {
        eventName: initialEventData.eventName || initialState.eventName,
        eventDate: initialEventData.date || null,
        eventStatus: initialEventData.status || initialState.eventStatus,
        livetrackSlug: initialEventData.livetrackSlug || null,
        heroImage: initialEventData.heroImage ?? initialState.heroImage,
        location: initialEventData.location || initialState.location,
        categories: Array.isArray(initialEventData.categories)
          ? initialEventData.categories
          : [],
        categoryGroups: Array.isArray(initialEventData.categoryGroups)
          ? initialEventData.categoryGroups
          : [],
      };

      try {
        localStorage.setItem(
          `cached_event_${eventId}`,
          JSON.stringify({
            eventName: normalized.eventName,
            // Cached too: this cache is read first on every later visit, and
            // without the date the hero of a category with no start time
            // read "Unknown Date".
            eventDate: normalized.eventDate,
            eventStatus: normalized.eventStatus,
            livetrackSlug: normalized.livetrackSlug,
            heroImage: normalized.heroImage,
            location: normalized.location,
            categories: normalized.categories,
            categoryGroups: normalized.categoryGroups,
          })
        );
      } catch {}

      return {
        ...initialState,
        ...normalized,
        loading: false,
        source: "EVENT_ROOT",
      };
    }

    return initialState;
  });
  const [isFetchingResults, setIsFetchingResults] = useState(false);
  // Denominators for "rank N of M" on the certificate and runner modal.
  const [rankedTotals, setRankedTotals] = useState({ overall: 0, male: 0, female: 0, category: null });
  const [searchQuery, setSearchQuery] = useState("");
  const [bibNumberInput, setBibNumberInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const debounceRef = useRef(null);
  const pollTimerRef = useRef(null);
  const fetchAbortRef = useRef(null);
  // Pagination state — same contract as Results.jsx so all results pages behave
  // identically: fetch 100 at a time, append as the sentinel scrolls into view.
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsTotalPages, setResultsTotalPages] = useState(1);
  const [resultsTotal, setResultsTotal] = useState(0);
  const sentinelRef = useRef(null);
  // Read by the poll interval, which is created once per category and would
  // otherwise close over stale searchQuery / resultsPage values.
  const searchRef = useRef("");
  const pageRef = useRef(1);
  searchRef.current = searchQuery;
  pageRef.current = resultsPage;
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";

  // Category groups (event.categoryGroups) show several stored categories as
  // one tab — AquaFitX's five individual age categories as INDIVIDUAL, with
  // the age groups behind the Overall / Age Group toggle. `allCategories`
  // resolves any id (group or member); `tabCategories` is what gets a button.
  const groupEntries = useMemo(
    () =>
      (state.categoryGroups || []).map((g) => ({
        ...(state.categories.find((c) => c.id === g.members?.[0]?.id) || {}),
        id: g.id,
        name: g.name,
      })),
    [state.categoryGroups, state.categories],
  );
  const allCategories = useMemo(() => [...state.categories, ...groupEntries], [state.categories, groupEntries]);
  const tabCategories = useMemo(() => {
    const groups = state.categoryGroups || [];
    if (!groups.length) return state.categories;
    const memberOf = new Map(groups.flatMap((g) => (g.members || []).map((m) => [m.id, g.id])));
    const out = [];
    state.categories.forEach((c) => {
      const gid = memberOf.get(c.id);
      if (!gid) out.push(c);
      else if (!out.some((x) => x.id === gid)) out.push(groupEntries.find((g) => g.id === gid));
    });
    return out;
  }, [state.categories, state.categoryGroups, groupEntries]);

  const tableCheckpoints = useMemo(() => {
    if (state.selectedCategory && state.selectedCategory !== "") {
      const selectedCategory = allCategories.find(
        (cat) => cat.id === state.selectedCategory
      );
      if (
        selectedCategory &&
        selectedCategory.checkpoints &&
        Array.isArray(selectedCategory.checkpoints)
      ) {
        // Same rule as Results.jsx: hide hand-entered mid-course splits, keep the
        // finish column that the standings actually rest on.
        const checkpoints = selectedCategory.checkpoints.filter(
          (cp) =>
            cp.locationId &&
            cp.name &&
            (cp.locationId === "finish" || cp.type === "startfinish")
        );
        return checkpoints;
      }
      return [];
    } else {
      const desiredOrder = [
        { locationId: "tanah_abang", name: "Tanah Abang" },
        { locationId: "mangku_sakti", name: "Mangku Sakti" },
        { locationId: "pos_2_rinjani", name: "POS 2 Rinjani" },
        { locationId: "gerbang_tngr", name: "Gerbang TNGR" },
        { locationId: "sebun_kedit", name: "Sebun Kedit" },
        { locationId: "rumah_adat", name: "Rumah Adat" },
        { locationId: "lapangan_bumbung", name: "Lapangan Bumbung" },
        { locationId: "finish", name: "FINISH" },
      ];

      const checkpointMap = new Map();
      state.categories.forEach((category) => {
        if (category.checkpoints && Array.isArray(category.checkpoints)) {
          category.checkpoints.forEach((cp) => {
            if (cp.locationId && cp.name) {
              checkpointMap.set(cp.locationId, cp);
            }
          });
        }
      });

      const checkpoints = [];
      const seenLocationIds = new Set();
      desiredOrder.forEach((desiredCp) => {
        const cp = checkpointMap.get(desiredCp.locationId);
        if (cp) {
          checkpoints.push({
            id: cp.id,
            locationId: cp.locationId,
            name: cp.name,
          });
          seenLocationIds.add(cp.locationId);
          checkpointMap.delete(cp.locationId);
        }
      });

      checkpointMap.forEach((cp) => {
        checkpoints.push({
          id: cp.id,
          locationId: cp.locationId,
          name: cp.name,
        });
      });

      return checkpoints;
    }
  }, [state.categories, allCategories, state.selectedCategory]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch ranked results from server (server-side ranking + age-group ranks)
  const fetchResults = useCallback(
    async ({ categoryId, search = "", page = 1, append = false } = {}) => {
      if (!eventId || !categoryId) return;
      if (fetchAbortRef.current) fetchAbortRef.current.abort();
      fetchAbortRef.current = new AbortController();
      const signal = fetchAbortRef.current.signal;
      setIsFetchingResults(true);
      try {
        const params = new URLSearchParams({
          eventId,
          category: categoryId,
          type: "age",
          page: String(page),
          limit: "100",
          ...(search ? { search } : {}),
        });
        const res = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/api/timing/results?${params}`,
          { signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setState((prev) => ({
          ...prev,
          // Infinite scroll fetches page 2 onward and expects them added, not
          // swapped in — replacing the array made scrolling reset the list to
          // the newest 100 rows instead of growing it.
          rankedResults: append
            ? [...prev.rankedResults, ...(data.results || [])]
            : data.results || [],
          top3Male:       data.top3Male     || [],
          // One gender only (e.g. DOUBLE WOMEN): no empty Top 3 panel and no
          // gender-rank column, as on the standard results page.
          singleGender:   data.singleGender ?? null,
          top3Female:     data.top3Female   || [],
          top3AgeGroups:  data.top3AgeGroups || {},
          totalResults:   data.total        || 0,
          categories:     data.categories   || prev.categories,
          categoryGroups: data.categoryGroups || prev.categoryGroups || [],
          eventName:      data.eventName    || prev.eventName,
          eventDate:      data.eventDate    || prev.eventDate,
          eventStatus:    data.eventStatus  || prev.eventStatus,
          heroImage:      data.heroImage    || prev.heroImage,
          location:       data.location     || prev.location,
          enableCertificate: data.enableCertificate ?? prev.enableCertificate ?? false,
          certificateBackground: data.certificateBackground ?? prev.certificateBackground ?? "",
          certificateLayout: data.certificateLayout ?? prev.certificateLayout ?? null,
          loading: false,
        }));
        setResultsPage(data.page || 1);
        setResultsTotalPages(data.totalPages || 1);
        setResultsTotal(data.total || 0);
        setRankedTotals({
          overall: data.rankedTotal ?? 0,
          male: data.rankedTotalMale ?? 0,
          female: data.rankedTotalFemale ?? 0,
          category: categoryId,
        });
      } catch (e) {
        if (e.name === "AbortError") return;
        console.error("[ResultsAge] fetchResults error:", e.message);
        setState((prev) => ({ ...prev, loading: false }));
      } finally {
        setIsFetchingResults(false);
      }
    },
    [eventId]
  );

  // Poll when category changes or on interval
  useEffect(() => {
    if (!state.selectedCategory) return;
    fetchResults({ categoryId: state.selectedCategory, search: searchQuery });
    const interval = state.eventStatus === "ongoing" ? 10_000 : 60_000;
    pollTimerRef.current = setInterval(() => {
      // Never poll while the user is searching or has scrolled past page 1:
      // the refetch overwrote the search hit they had just typed (it appeared,
      // then vanished on the next tick) and discarded loaded pages.
      if (searchRef.current) return;
      if (pageRef.current > 1) return;
      fetchResults({ categoryId: state.selectedCategory, search: "" });
    }, interval);
    return () => clearInterval(pollTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedCategory, state.eventStatus]);

  // Re-fetch on search change
  useEffect(() => {
    if (!state.selectedCategory) return;
    fetchResults({ categoryId: state.selectedCategory, search: searchQuery });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (tabCategories.length > 0) {
      const stored = localStorage.getItem("smm_last_category");
      const validIds = new Set(tabCategories.map((c) => c.id));
      const defaultCat = validIds.has("7K")
        ? "7K"
        : tabCategories[0]?.id || "";
      if (!stored || !validIds.has(stored)) {
        localStorage.setItem("smm_last_category", defaultCat);
        setState((prev) => ({ ...prev, selectedCategory: defaultCat }));
      }
    }
  }, [tabCategories]);

  const handleCategoryChange = useCallback(
    (categoryId) => {
      if (categoryId === state.selectedCategory) return;
      setState((prev) => ({ ...prev, selectedCategory: categoryId }));
      localStorage.setItem("smm_last_category", categoryId);
      setSearchQuery("");
      setBibNumberInput("");
    },
    [state.selectedCategory]
  );

  const handleInputChange = useCallback((event) => {
    const input = event.target.value;
    setBibNumberInput(input);
    setIsTyping(true);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setSearchQuery(input);
      setIsTyping(false);
    }, 500);
  }, []);

  const [sortConfig, setSortConfig] = useState({
    key: "overallRank",
    direction: "ascending",
  });

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const formatResults = useCallback(
    (runner) => {
      const category = allCategories.find(
        (cat) => cat.id === runner.categoryId
      );
      if (!category || !tableCheckpoints.length) {
        return tableCheckpoints.map(() => ({ time: "-", isCOT: false }));
      }

      return tableCheckpoints.map((cp) => {
        let checkpointData = runner.checkpoints?.[cp.locationId];
        
        // For finish checkpoint, check manual status first
        if (cp.locationId === "finish") {
          // Check if runner has a manual status (DNF/DNS/DSQ)
          if (runner.status === "DNF") {
            return { time: "DNF", isCOT: false, isDNF: true };
          }
          if (runner.status === "DNS") {
            return { time: "DNS", isCOT: false, isDNS: true };
          }
          if (runner.status === "DSQ") {
            return { time: "DSQ", isCOT: false, isDSQ: true };
          }
          
          // Check for finishTime fallback
          if (!checkpointData && runner.finishTime) {
            if (runner.finishTime.includes("COT")) {
              const cleanTime = runner.finishTime
                .replace("COT (", "")
                .replace(")", "");
              return { time: `COT (${cleanTime})`, isCOT: true };
            }
            if (runner.finishTime) {
              checkpointData = { time: runner.finishTime };
            }
          }
          
          // No finish time
          if (!checkpointData?.time || !moment(checkpointData.time).isValid()) {
            return { time: "-", isCOT: false };
          }
          
          // Has finish time - check for COT
          let isCOT = false;
          if (category?.cutOffTime) {
            const cutOffMoment = moment(
              category.cutOffTime.toDate
                ? category.cutOffTime.toDate()
                : category.cutOffTime
            ).tz(userTimeZone);
            const finishMoment = moment(checkpointData.time).tz(userTimeZone);
            isCOT =
              cutOffMoment.isValid() && finishMoment.isAfter(cutOffMoment);
          }
          const { time } = formatTime(checkpointData.time, isCOT);
          const displayValue = getFinishDisplay(runner.finishDuration, runner.gunTime, time);
          const displayText = isCOT ? `COT (${displayValue})` : displayValue;
          return { time: displayText, isCOT };
        }

        // Non-finish checkpoints
        if (!checkpointData?.time || !moment(checkpointData.time).isValid()) {
          return { time: "-", isCOT: false };
        }

        const { time } = formatTime(checkpointData.time, false);
        return { time, isCOT: false };
      });
    },
    [allCategories, tableCheckpoints, userTimeZone]
  );

  // ── Infinite scroll: sentinel enters viewport → append next page.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingResults) {
          // Derive the next page from a ref, not inside setResultsPage: fetching
          // in the updater makes it a side effect (React may run updaters twice,
          // double-loading a page) and the counter kept climbing past the last
          // page, so the sentinel stopped firing altogether.
          const next = pageRef.current + 1;
          if (next > resultsTotalPages) return;
          pageRef.current = next;
          setResultsPage(next);
          fetchResults({
            categoryId: state.selectedCategory,
            page: next,
            search: searchRef.current,
            append: true,
          });
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isFetchingResults, resultsTotalPages, fetchResults, state.selectedCategory, searchQuery]);

  // Bands come from the server response, which applies whatever the event was
  // configured with. Falling back to the masters constant keeps events saved
  // before bands were configurable rendering exactly as before.
  const ageGroupLabels = useMemo(() => {
    const keys = Object.keys(state.top3AgeGroups || {});
    return keys.length ? keys : AGE_GROUPS;
  }, [state.top3AgeGroups]);

  // The age view only means something for a category whose runners carry an
  // age group. A doubles or relay category in the same event has none, so the
  // toggle is hidden there and the overall view shown.
  const hasAgeGroups = useMemo(
    () => Object.values(state.top3AgeGroups || {}).some((g) => g?.male?.length || g?.female?.length),
    [state.top3AgeGroups],
  );
  const showAge = state.viewMode === "age" && hasAgeGroups;
  // For a category group the age view lists every athlete of each age group,
  // not just its top 3: each group is a category in its own right, and its
  // full standings were otherwise nowhere on the page.
  const isGroupTab = (state.categoryGroups || []).some((g) => g.id === state.selectedCategory);
  const fullAgeLists = showAge && isGroupTab;
  const ageListOf = (group, gender) =>
    state.rankedResults
      .filter((r) => r.ageGroup === group && r.gender === gender && r.ageGroupRank > 0)
      .sort((a, b) => a.ageGroupRank - b.ageGroupRank)
      .map((r) => ({ ...r, genderRank: r.ageGroupRank }));

  const filteredResults = useMemo(() => {
    return state.rankedResults
      .filter((runner) => {
        const matchesBIB =
          !searchQuery ||
          runner.bib?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesName =
          !searchQuery ||
          runner.name?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory =
          state.selectedCategory === "" ||
          state.selectedCategory === runner.categoryId;
        return (matchesBIB || matchesName) && matchesCategory;
      })
      .sort((a, b) => {
        // Status runners (DNF/DNS/DSQ) should be at the very bottom
        const statusOrder = { DNF: 1, DNS: 2, DSQ: 3 };
        const aStatus = statusOrder[a.status] || 0;
        const bStatus = statusOrder[b.status] || 0;
        if (aStatus && !bStatus) return 1;
        if (!aStatus && bStatus) return -1;
        if (aStatus && bStatus) return aStatus - bStatus; // Order: DNF, DNS, DSQ
        
        // COT should be below finished but above DNF, sorted by duration
        if (a.isCOT && !b.isCOT) return 1;
        if (!a.isCOT && b.isCOT) return -1;
        if (a.isCOT && b.isCOT) {
          // Use finishDuration for proper duration-based sorting
          return timeToSeconds(a.finishDuration || a.finishTime) - timeToSeconds(b.finishDuration || b.finishTime);
        }

        if (sortConfig.key === "gender") {
          const genderA = a.gender || "";
          const genderB = b.gender || "";
          if (genderA < genderB) {
            return sortConfig.direction === "ascending" ? -1 : 1;
          }
          if (genderA > genderB) {
            return sortConfig.direction === "ascending" ? 1 : -1;
          }
          return rankOrder(a) - rankOrder(b);
        }

        return (a.overallRank || Infinity) - (b.overallRank || Infinity);
      });
  }, [state.rankedResults, searchQuery, state.selectedCategory, sortConfig]);

  const getRunnerStatus = useCallback(
    (runner, category) => {
      const finishTime = runner.checkpoints?.finish?.time || runner.finishTime;
      if (finishTime && moment(finishTime).isValid()) {
        if (category?.cutOffTime) {
          const finishMoment = moment(finishTime).tz(userTimeZone);
          const cutOffMoment = moment(
            category.cutOffTime.toDate
              ? category.cutOffTime.toDate()
              : category.cutOffTime
          ).tz(userTimeZone);
          if (cutOffMoment.isValid() && finishMoment.isAfter(cutOffMoment)) {
            return "COT";
          }
        }
        return "FINISHED";
      }

      const hasCheckpoints = Object.keys(runner.checkpoints || {}).some(
        (cp) =>
          runner.checkpoints[cp]?.time &&
          moment(runner.checkpoints[cp].time).isValid()
      );
      if (hasCheckpoints) {
        return "ON RACE";
      }

        return "DNF";
    },
    [userTimeZone]
  );

  const [selectedRunner, setSelectedRunner] = useState(null);

  const handleNameClick = useCallback(
    (runner) => { setSelectedRunner(runner); },
    []
  );

  if (state.error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <p className="text-red-500 font-oxanium">{state.error}</p>
      </div>
    );
  }

  if (state.loading) {
    return <PageLoader />;
  }

  return (
    <section className="min-h-screen mt-10 bg-white mb-10">
      <div className="container mx-auto px-4">
        <Hero
          selectedCategory={state.selectedCategory}
          categories={tabCategories}
          eventName={state.eventName}
          eventDate={state.eventDate}
          eventStatus={state.eventStatus}
          heroImage={state.heroImage}
          location={state.location}
          source={state.source}
          eventId={eventId}
          slug={slug}
        />
        {/* Live Map Integration */}
        {state.livetrackSlug && (
          <div className="mt-4">
             <div className="bg-white rounded-card border border-slate-200 overflow-hidden">
                <div
                  className="px-4 py-4 bg-gray-50 flex justify-between items-center cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => setState(prev => ({ ...prev, showMap: !prev.showMap }))}
                >
                   <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                      <h3 className="font-bold text-gray-800 font-oxanium tracking-wide">
                          LIVE TRACKING MAP
                      </h3>
                   </div>
                   <div className="text-sm text-gray-500 font-oxanium">
                      {state.showMap ? "Click to Hide" : "Click to Show"}
                   </div>
                </div>
                
                {state.showMap && (
                    <div className="w-full h-[600px] relative bg-gray-100">
                      <iframe 
                        src={state.livetrackSlug?.startsWith('http') 
                          ? `${state.livetrackSlug}${state.livetrackSlug.includes('?') ? '&' : '?'}embed=true` // Smart Append
                          : `https://tracking.vionixasia.com/map/${state.livetrackSlug}?embed=true`
                        } 
                        className="w-full h-full border-0"
                        title="Live Tracking Map"
                        allow="geolocation"
                      />
                    </div>
                )}
             </div>
          </div>
        )}
        <div className="mb-4 mt-4 flex flex-wrap justify-between items-center gap-2">
          <div className="flex flex-wrap gap-2">
            <CategoryPicker categories={tabCategories} selected={state.selectedCategory} onSelect={handleCategoryChange} />

            {/* Divider */}
            <div className="w-px h-8 bg-gray-300 mx-1 self-center hidden md:block"></div>
             {/* Age Group Toggle */}
            {hasAgeGroups && (
            <div className="flex items-center gap-2 bg-gray-100 rounded-card px-3 py-1.5 border border-gray-200">
                <span className={`text-sm font-bold ${state.viewMode === "gender" ? "text-gray-900" : "text-gray-400"}`}>Overall</span>
                <div 
                    className={`nav-toggle relative w-12 h-6 rounded-full cursor-pointer transition-colors duration-300 ${state.viewMode === "age" ? "bg-teal-500" : "bg-gray-300"}`}
                    onClick={() => setState(prev => ({ ...prev, viewMode: prev.viewMode === "age" ? "gender" : "age" }))}
                >
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${state.viewMode === "age" ? "translate-x-6" : "translate-x-0"}`}></div>
                </div>
                <span className={`text-sm font-bold ${state.viewMode === "age" ? "text-gray-900" : "text-gray-400"}`}>Age Group</span>
            </div>
            )}
          </div>
          <InputField
            type="text"
            value={bibNumberInput}
            onChange={handleInputChange}
            placeholder="Search BIB or Name"
            className="text-center shadow-sm"
            wrapperClassName="w-full md:w-64"
          />
        </div>

        {/* Top 3 Gender */}
        {!showAge && !state.singleGender && (
            <div className="flex flex-col md:flex-row gap-4 mb-4">
            <Top3Table
                title="Top 3 Men"
                results={state.top3Male}
                onNameClick={handleNameClick}
            />
            <Top3Table
                title="Top 3 Women"
                results={state.top3Female}
                onNameClick={handleNameClick}
            />
            </div>
        )}

        {/* Top 3 Age Groups */}
        {showAge && ageGroupLabels.map((group) => {
            const data = fullAgeLists
              ? { male: ageListOf(group, "male"), female: ageListOf(group, "female") }
              : state.top3AgeGroups?.[group] || { male: [], female: [] };
            return (
            <div key={group} className="mb-6">
                <h3 className="text-md font-bold text-gray-700 mb-2 px-1">
                    AGE GROUP: {group}
                </h3>
                <div className="flex flex-col md:flex-row gap-4 mb-4">
                    <Top3Table
                        title={fullAgeLists ? "Men" : `Top 3 Men`}
                        results={data.male}
                        onNameClick={handleNameClick}
                    />
                    <Top3Table
                        title={fullAgeLists ? "Women" : `Top 3 Women`}
                        results={data.female}
                        onNameClick={handleNameClick}
                    />
                </div>
            </div>
            );
        })}

        {/* Main Table — the overall standings. Hidden while full age-group
            lists are shown, which already list every athlete. */}
        <div className={fullAgeLists ? "hidden" : "w-full"}>
          {/* Desktop Table - Hidden on Mobile */}
          <div className="hidden md:block overflow-x-auto pb-8">
            <table className="min-w-full border-separate table-auto" style={{ borderSpacing: "0 8px" }}>
              <thead>
                <tr>
                  <th className="bg-slate-50 px-2 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-[50px] rounded-l-card border-y border-l border-slate-200">
                    Rank
                  </th>
                  <th className="bg-slate-50 px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider max-w-[150px] border-y border-slate-200">
                    Name
                  </th>
                  <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                    Nationality
                  </th>
                  <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                    Bib
                  </th>
                  <th
                    className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors border-y border-slate-200"
                    onClick={() => requestSort("gender")}
                  >
                    Gender
                    {sortConfig.key === "gender" && (
                      <span className="ml-1">
                        {sortConfig.direction === "ascending" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                  {!state.singleGender && (
                  <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                    Rank Gender
                  </th>
                  )}
                  {tableCheckpoints
                    .filter((cp) => cp.locationId !== "finish")
                    .map((cp) => (
                      <th
                        key={cp.locationId}
                        className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200"
                      >
                        {cp.name.toUpperCase()}
                      </th>
                    ))}
                  <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider rounded-r-card border-y border-r border-slate-200">
                    FINISH
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.length === 0 && !isFetchingResults ? (
                  <tr>
                    <td
                      colSpan={
                        6 +
                        tableCheckpoints.filter(
                          (cp) => cp.locationId !== "finish"
                        ).length
                      }
                      className="px-4 py-4 text-center text-sm text-gray-600"
                    >
                      No race results available for this category.
                    </td>
                  </tr>
                ) : (
                  filteredResults.map((result, index) => {
                    const { code: flagCode, display: nationalityDisplay } =
                      getFlagCode(result.nationality);
                    const checkpointEntries = formatResults(result);
                    const finishEntry =
                      checkpointEntries[checkpointEntries.length - 1] || {
                      // A hand-timed result has no mat columns: its recorded
                      // duration is the finish (as on the standard results page).
                      time: finishDisplay(result, allCategories.find((c) => c.id === result.categoryId) || null),
                      isCOT: false,
                    };

                    return (
                      <tr
                        key={`${result.id || "unknown"}-${index}`}
                        className="hover:-translate-y-[2px] transition-transform shadow-sm group"
                      >
                        <td className="bg-white px-2 py-4 text-sm text-slate-600 text-center w-[60px] rounded-l-card border-y border-l border-slate-200 group-hover:bg-slate-50 transition-colors">
                          {result.overallRank > 0 ? (
                            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 bg-slate-200 text-slate-700 rounded-full font-bold text-xs">
                              {result.overallRank}
                            </span>
                          ) : "-"}
                        </td>
                        <td
                          className="bg-white px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900 uppercase max-w-[150px] truncate cursor-pointer hover:text-teal-600 border-y border-slate-200 group-hover:bg-slate-50 transition-colors"
                          onClick={() => handleNameClick(result)}
                        >
                          {capitalizeWords(result.name) || "-"}
                        </td>
                        <td className="bg-white px-4 py-4 text-sm text-slate-600 text-center border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                          <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-slate-500">
                            <img
                              src={`https://flagcdn.com/w20/${flagCode}.png`}
                              onError={(e) => { e.target.src = "https://flagcdn.com/w20/un.png"; }}
                              className="w-4 h-3 mr-1 border border-slate-200 rounded object-cover"
                              alt={nationalityDisplay}
                            />
                            {nationalityDisplay || "-"}
                          </span>
                        </td>
                        <td className="bg-white px-4 py-4 whitespace-nowrap text-sm text-slate-600 text-center border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                          <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-slate-500">
                            {result.bib ? `BIB ${result.bib}` : "-"}
                          </span>
                        </td>
                        <td className="bg-white px-4 py-4 whitespace-nowrap text-sm text-slate-600 text-center max-w-[150px] border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                          <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-slate-500">
                            {formatGender(result.gender)}
                          </span>
                        </td>
                        {!state.singleGender && (
                        <td className="bg-white px-4 py-4 text-sm text-slate-600 text-center border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                          <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-slate-500">
                            {result.genderRank > 0 ? result.genderRank : "-"}
                          </span>
                        </td>
                        )}
                        {checkpointEntries
                          .filter((_, idx) => tableCheckpoints[idx]?.locationId !== "finish")
                          .map((entry, idx) => (
                            <td
                              key={idx}
                              className={`bg-white px-4 py-4 text-center text-sm border-y border-slate-200 group-hover:bg-slate-50 transition-colors ${entry.isCOT ? "text-red-600" : "text-slate-600"} whitespace-nowrap`}
                            >
                              {entry.time}
                            </td>
                          ))}
                        <td
                          className={`bg-white px-4 py-4 text-center text-sm font-semibold rounded-r-card border-y border-r border-slate-200 group-hover:bg-slate-50 transition-colors ${
                            finishEntry?.isCOT ? "text-red-600" :
                            finishEntry?.isDNF ? "text-orange-600" :
                            finishEntry?.isDNS ? "text-slate-500" :
                            finishEntry?.isDSQ ? "text-red-600" :
                            "text-slate-600 font-normal"
                          } whitespace-nowrap`}
                        >
                          {finishEntry?.time || "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View - Hidden on Desktop */}
          <div className="md:hidden space-y-3 pb-6">
            
            {/* Mobile Results */}
            {filteredResults.length === 0 && !isFetchingResults ? (
              <div className="px-4 py-6 text-center text-sm text-slate-600">
                No race results available for this category.
              </div>
            ) : (
              filteredResults.map((result, index) => {
                const { code: flagCode, display: nationalityDisplay } =
                  getFlagCode(result.nationality);
                const checkpointEntries = formatResults(result);
                const finishEntry = checkpointEntries[checkpointEntries.length - 1] || {
                  // A hand-timed result has no mat columns: its recorded
                  // duration is the finish (as on the standard results page).
                  time: finishDisplay(result, allCategories.find((c) => c.id === result.categoryId) || null),
                  isCOT: false,
                    };

                return (
                  <div
                    key={`mobile-${result.id || "unknown"}-${index}`}
                    className="bg-white rounded-card border border-slate-200 shadow-sm p-2 overflow-hidden"
                  >
                    {/* Row 1: Rank, Name, Finish - using table-fixed for consistent widths */}
                    <table className="min-w-full table-fixed">
                      <tbody>
                        <tr>
                          {/* Rank in a pill, matching the other results pages:
                              flat text at one weight gave the eye nothing to
                              land on first. */}
                          <td className="w-12 py-2 text-sm font-medium text-slate-700 text-center">
                            {result.overallRank > 0 ? (
                              <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 bg-slate-100 text-slate-700 rounded-full font-bold text-xs">
                                {result.overallRank}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td
                            className="py-2 text-sm font-bold text-slate-900 uppercase truncate max-w-0 cursor-pointer"
                            onClick={() => handleNameClick(result)}
                          >
                            {capitalizeWords(result.name) || "-"}
                          </td>
                          <td
                            className={`w-24 py-2 text-sm font-medium text-right pr-4 ${
                              result.isCOT ? "text-red-600" : 
                              result.status === "DNF" ? "text-orange-600" :
                              result.status === "DNS" ? "text-slate-500" :
                              result.status === "DSQ" ? "text-red-600" :
                              "text-gray-700"
                            }`}
                          >
                            {result.status === "DNF" ? "DNF" :
                             result.status === "DNS" ? "DNS" :
                             result.status === "DSQ" ? "DSQ" :
                             result.finishDuration || "-"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    
                    {/* Row 2: Additional Info */}
                    <div className="flex items-center pb-2 gap-3 ml-12 border-t border-slate-200/50 pt-2 pr-4">
                      {/* Flag */}
                      <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-slate-500">
                            <img
                              src={`https://flagcdn.com/w20/${flagCode}.png`}
                              onError={(e) => { e.target.src = "https://flagcdn.com/w20/un.png"; }}
                              className="w-4 h-3 mr-1 border border-slate-200 rounded object-cover"
                              alt={nationalityDisplay}
                            />
                            {nationalityDisplay || "-"}
                          </span>
                      {/* Bib */}
                      <span className="text-xs text-slate-500">
                        BIB {result.bib || "-"}
                      </span>
                      {/* Gender Icon Only */}
                      <span className="text-xs text-slate-500">
                        <FontAwesomeIcon 
                          icon={result.gender?.toLowerCase() === 'male' || result.gender?.toLowerCase() === 'm' ? faMars : faVenus} 
                        />
                      </span>
                      {/* Spacer to push rank to right */}
                      <span className="flex-1"></span>
                      {/* Gender Rank - aligned right with w-20 like Finish */}
                      {!state.singleGender && (
                      <span className="text-xs text-gray-500 text-right whitespace-nowrap">
                        #<span className="font-medium text-slate-700">{result.genderRank > 0 ? result.genderRank : "-"}</span>
                      </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Infinite scroll sentinel — matches Results.jsx so every results
              page loads in pages of 100 instead of one oversized request. */}
          <div ref={sentinelRef} className="pb-6 pt-2 min-h-[1px]">
            {resultsTotal > 0 && (
              resultsPage < resultsTotalPages ? (
                <div className="flex flex-col items-center gap-1 py-4">
                  {isFetchingResults && (
                    <div className="w-4 h-4 rounded-full border-2 border-slate-300 border-t-slate-500 animate-spin" />
                  )}
                  <span className="text-xs text-slate-400">
                    Showing {filteredResults.length.toLocaleString()} of {resultsTotal.toLocaleString()}
                  </span>
                </div>
              ) : (
                <p className="text-center text-xs text-slate-400 py-2">
                  {resultsTotal.toLocaleString()} result{resultsTotal !== 1 ? "s" : ""}
                </p>
              )
            )}
          </div>
        </div>
      </div>

      {selectedRunner && (
        <RunnerDetailsModal
          runner={selectedRunner}
          rankedTotals={rankedTotals}
          onClose={() => setSelectedRunner(null)}
          categories={allCategories}
          eventId={eventId}
          eventData={{
            enableCertificate: state.enableCertificate || false,
            eventName: state.eventName,
            // Without these the renderer silently falls back to the stock design.
            certificateBackground: state.certificateBackground || "",
            certificateLayout: state.certificateLayout || null,
          }}
        />
      )}

      {/* Footer */}

    </section>
  );
};

export default ResultsAge;
